import { Accessor, createEffect, createResource, createSignal, JSX, Match, onMount, Setter, Show, splitProps, Switch } from "solid-js"
import { createStore } from "solid-js/store"
import { AlchemySigner, disconnect, getSigner, getSmartAccountClient, reconnect, watchSigner, watchSmartAccountClient } from "@account-kit/core"
import { createSignalFromExternalStore } from "./lib/createSignalFromExternalStore"
import { config } from "./lib/config"
import { Button, ButtonProps } from "../../ui/Button"
import { Portal } from "solid-js/web"
import LoadingSpinner from "../LoadingSpinner"
import Hr from "../Hr"
import api, { LoginResponse } from "../../swarm.apiv1"
import { OauthCancelledError, OauthFailedError } from "@account-kit/signer"
import { swarmVariant } from "../../lib/swarmVariant"

type Props = {
	isOpen: Accessor<boolean>
	setIsOpen: Setter<boolean>
	setUserEoa: Setter<`0x${string}` | null>
	userEoa: Accessor<`0x${string}` | null>
	dataset: string
}

type Initial = {
	type: "Initial"
	email: string
	error: string
}

type VerifyOtp = {
	type: "VerifyOtp"
	otp: string
	email: string
	error: string
	resent: boolean
}

type AuthenticatingOauth = {
	type: "AuthenticatingOauth"
	error: string
}

type Complete = {
	type: "Complete"
}

type AuthState = Initial | VerifyOtp | AuthenticatingOauth | Complete

const initState: AuthState = { type: "Initial", email: "", error: "" }

export function AuthModal(props: Props) {
	const accountParams = { type: "ModularAccountV2" } as const
	const client = createSignalFromExternalStore(() => getSmartAccountClient(accountParams, config), watchSmartAccountClient(accountParams, config))
	const signer = createSignalFromExternalStore<AlchemySigner | undefined>(() => getSigner(config) ?? undefined, watchSigner(config))
	const [authState, setAuthState] = createStore<AuthState>({ ...initState })
	// The user EOA will be availabed in the auth process before the user
	// is successfully logged in with the dashboard backend. Keep state
	// internal until the backend login is completed, at which point
	// the AuthModal can propagate the user EOA.
	const [provisionalEoa, setProvisionalEoa] = createSignal<`0x${string}` | null>(null)
	const [loginData] = createResource(provisionalEoa, login)

	onMount(() => {
		reconnect(config)
	})

	createEffect(() => {
		const userEoa = client()?.address

		if (!userEoa) {
			return
		}

		setProvisionalEoa(userEoa)
		setAuthState({ type: "Complete" })
	})

	const closeModal = () => {
		setAuthState({ ...initState })
		props.setIsOpen(false)
	}

	createEffect(() => {
		if (loginData.loading) {
			return
		}

		if (loginData.error) {
			// `createResoure`'s errors are untyped but the solid-js docs state
			// that `data.error` will be some unknown `data.error.cause` or something
			// with a string at `message`
			const message: string = loginData.error.cause ? "Something unexpected happened while logging in" : loginData.error.message
			setAuthState({ type: "Initial", email: "", error: message })
			return
		}

		if (loginData()) {
			props.setUserEoa(provisionalEoa())
		}
	})

	createEffect(() => {
		let timeoutHandler: ReturnType<typeof setTimeout> | null = null

		// Set `resent` back to false after 2 seconds so the user
		// can send themselves another otp code multiple times
		if (authState.type === "VerifyOtp" && authState.resent) {
			timeoutHandler = setTimeout(() => {
				setAuthState({ ...authState, resent: false })
			}, 2 * 1000)
		}

		return () => {
			if (timeoutHandler) {
				clearTimeout(timeoutHandler)
			}
		}
	})

	createEffect(() => {
		let timeoutHandler: ReturnType<typeof setTimeout> | null = null

		// Delay closing the modal to provide explicit confirmation
		// to the user that they successfully logged in
		if (authState.type === "Complete") {
			timeoutHandler = setTimeout(() => {
				closeModal()
			}, 2 * 1000)
		}

		return () => {
			if (timeoutHandler) {
				clearTimeout(timeoutHandler)
			}
		}
	})

	const authenticateWithOauth = (populatedSigner: AlchemySigner) => {
		populatedSigner
			.authenticate({
				type: "oauth",
				authProviderId: "google",
				mode: "popup",
			})
			.catch((authErr) => {
				// Send user back if they close the popup window
				if (authErr instanceof OauthCancelledError) {
					setAuthState({ ...initState })
					return
				}

				// Send user back if they don't allow alchemy to connect with their Google account
				if (authErr instanceof OauthFailedError && authErr.message.includes("access_denied")) {
					setAuthState({ ...initState })
					return
				}
				setAuthState({ type: "AuthenticatingOauth", error: "message" in authErr ? authErr.message : "An unexpected error occurred" })
			})

		setAuthState({ type: "AuthenticatingOauth", error: "" })
	}

	return (
		<Show when={props.isOpen() && signer()}>
			{(signer) => {
				return (
					<Portal>
						<aside class="fixed top-0 left-0 min-h-screen min-w-screen flex flex-col items-center justify-center bg-black/50 z-99">
							<div class="relative bg-base-200 text-base-content w-[80vw] md:w-[30vw] h-[80vh] md:h-[480px] flex flex-col p-8 items-start border-2 border-base-content border-dotted">
								<button onClick={closeModal} class="text-3xl ml-auto">
									&times;
								</button>
								<Show when={swarmVariant !== "genrl-swarm"}>
									<h1 class="uppercase text-2xl tracking-[0.25em] mb-4">Gensyn Testnet: {props.dataset}</h1>
								</Show>
								<Show when={swarmVariant === "genrl-swarm"}>
									<img class="mb-8 w-80 mx-auto block" src="/images/gen-RLSwarm-UI-logo-.png" alt="RL Swarm logo" />
								</Show>
								<div class="flex flex-col justify-between w-full mt-4 items-start space-y-2">
									<div class="flex flex-col items-start gap-1 w-full items-start">
										<Switch>
											<Match when={authState.type === "Initial" && authState} keyed>
												{(state) => {
													return (
														<form
															class="w-full flex flex-col space-y-2"
															onSubmit={(e) => {
																e.preventDefault()
																if (!state.email.trim()) {
																	return
																}

																signer().authenticate({
																	type: "email",
																	email: state.email,
																})

																setAuthState({ type: "VerifyOtp", otp: "", email: state.email, resent: false })
															}}
														>
															<Hr glyph="dot" fill="base-content" />
															<h1 class="mt-4 text-lg uppercase tracking-[0.25em]">Enter your email address</h1>
															<input
																type="email"
																value={state.email}
																onInput={(e) => {
																	setAuthState({ ...state, email: e.currentTarget.value })
																}}
																onKeyDown={(e) => {
																	if (e.key === "Enter") {
																		e.preventDefault()
																		e.stopPropagation()
																		const target = e.target as HTMLInputElement
																		target.form?.requestSubmit()
																	}
																}}
																placeholder="EMAIL@EXAMPLE.COM"
																class="mb-8 w-full bg-base-100 border border-base-content p-2 flex-grow focus:outline-none focus:ring-0 focus:border-accent"
															/>
															<Button type="submit" colorway="primary" width="full" content={"Continue with Email"} />
															<Hr glyph="dot" fill="base-content" />
															<p class="text-md uppercase tracking-[0.25em] mb-4">or</p>
															<Button colorway="primary" width="full" onClick={() => authenticateWithOauth(signer())} content={"Continue with Google"} />
															<Show when={state.error}>
																<p class="font-mono text-red-600 lowercase">something unexpected happened, please try again</p>
															</Show>
														</form>
													)
												}}
											</Match>
											<Match when={authState.type === "AuthenticatingOauth" && authState} keyed>
												{(state) => {
													const loadingState = (
														<>
															<LoadingSpinner message="Continue with Google" />
															<p class="text-md uppercase tracking-[0.25em]">follow the steps in the popup window to sign in with Google</p>
														</>
													)
													return (
														<Show when={state.error} fallback={loadingState}>
															<div class="w-full flex flex-col space-y-2">
																<p class="text-md uppercase tracking-[0.25em]">couldnt' connect to Google</p>
																<p class="font-mono text-red-600 lowercase">an unexpected error occurred connecting your account</p>
																<Button colorway="primary" width="full" onClick={() => authenticateWithOauth(signer())} content={"Try again"} />
																<Button colorway="primary" width="full" onClick={() => setAuthState({ ...initState })} content={"Use another method"} />
															</div>
														</Show>
													)
												}}
											</Match>
											<Match when={authState.type === "VerifyOtp" && authState} keyed>
												{(state) => {
													return (
														<form
															class="w-full flex flex-col space-y-2"
															onSubmit={(e) => {
																e.preventDefault()
																if (!state.otp.trim()) {
																	return
																}

																signer()
																	.authenticate({
																		type: "otp",
																		otpCode: state.otp,
																	})
																	.catch((authErr) => {
																		try {
																			// Unfortunately some Alchemy error messages appear themselves to be
																			// stringified JSON. To get the original error message this handler
																			// parses the wrapping error message whose `error` property ought
																			// to be some stringified JSON. Then parse that `error` property
																			// to reconstruct the original error object.
																			const parsedErrWrapper = JSON.parse(authErr.message)
																			const parsedErr = JSON.parse(parsedErrWrapper.error)
																			if ("message" in parsedErr && parsedErr.message === "Invalid OTP code") {
																				setAuthState({ ...state, otp: "", error: "The code you entered is incorrect" })
																				return
																			}

																			setAuthState({ ...state, otp: "", error: "An unexpected error occurred" })
																		} catch (err) {
																			setAuthState({ ...state, otp: "", error: "An unexpected error occurred" })
																		}
																	})
															}}
														>
															<p class="uppercase text-md">Enter the verification code sent to your email address</p>
															<input
																type="text"
																placeholder="••••••"
																maxlength="6"
																minlength="6"
																value={state.otp}
																onInput={(e) => {
																	setAuthState({ ...state, otp: e.currentTarget.value, error: "" })
																}}
																class="w-full border bg-base-100 border border-base-content text-base-content p-2 flex-grow focus:outline-none focus:ring-0 focus:border-accent"
															/>
															<Button colorway="primary" width="full" type="submit" content={"Verify Code"} />
															<Show when={state.error}>
																<p class="font-mono text-red-600 lowercase">{state.error}</p>
															</Show>
															<div class="flex flex-row items-center gap-1">
																<Show when={!state.resent} fallback={<p class="uppercase mt-4">Code resent</p>}>
																	<div class="mt-4">
																		<p class="uppercase">
																			didn't receive the email?&nbsp;
																			<span
																				class="underline cursor-pointer"
																				onClick={() => {
																					signer().authenticate({
																						type: "email",
																						email: state.email,
																					})
																					setAuthState({ ...state, resent: true })
																				}}
																			>
																				resend
																			</span>
																		</p>
																	</div>
																</Show>
															</div>
														</form>
													)
												}}
											</Match>
											<Match when={authState.type === "Complete" && authState} keyed>
												<LoadingSpinner message="Success! Logging in" />
											</Match>
										</Switch>
									</div>
								</div>
							</div>
						</aside>
					</Portal>
				)
			}}
		</Show>
	)
}

type LogoutButtonProps = {
	setUserEoa: Setter<`0x${string}` | null>
} & Omit<ButtonProps, "content">

export function LogOutButton(props: LogoutButtonProps) {
	const [local, rest] = splitProps(props, ["setUserEoa"])
	console.log(rest)

	const signer = createSignalFromExternalStore<AlchemySigner | undefined>(() => getSigner(config) ?? undefined, watchSigner(config))
	const [loggingOut, setLoggingOut] = createSignal<boolean>(false)
	// TODO: bubble up to auth modal / accept a setter as a prop for this
	const [_error, setError] = createSignal<string | null>(null)

	const onClick = async () => {
		setLoggingOut(true)
		setError(null)

		try {
			await disconnect(config)
			await api.logout()
			local.setUserEoa(null)
		} catch (e) {
			console.error(e)
			setError(e as string)
		}

		setLoggingOut(false)
	}
	return <Button disabled={!signer() || loggingOut() === true} onClick={onClick} content="Log out" {...rest} />
}

async function login(eoa: string): Promise<LoginResponse | undefined> {
	try {
		const res = await api.login(eoa)

		return res
	} catch (err) {
		console.error("an error occurred attempting to login:", err)
		return undefined
	}
}
